import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAPI, setAuthTokenCookie } from '../services/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Function to update user state from verification response
  const updateUserFromVerification = (response) => {
    const token = localStorage.getItem('authToken');
    if (token && response.status === 200) {
      setUser({ 
        token, 
        user_id: response.data.user_id,
        userType: response.data.userType,
        ...response.data 
      });
    }
  };

  // Check if user is already logged in on app start
  useEffect(() => {
    const checkAuthStatus = async () => {
      const token = localStorage.getItem('authToken');
      if (token) {
        try {
          // Verify token with server ONCE on app start
          console.log('Verifying token on app start...');
          const response = await authAPI.verify();
          if (response.status === 200) {
            // Include role from response (userType field from verify endpoint)
            const userData = {
              token,
              ...response.data,
              role: response.data.userType || response.data.role // Map userType to role for consistency
            };
            setUser(userData);
            console.log('Token verified successfully', userData);
          } else {
            // Token is invalid, clear it
            localStorage.removeItem('authToken');
            setUser(null);
          }
        } catch (error) {
          // Token verification failed, clear it
          localStorage.removeItem('authToken');
          setUser(null);
          console.log('Token verification failed:', error.response?.status);
        }
      } else {
        console.log('No token found in localStorage');
      }
      setLoading(false);
    };

    checkAuthStatus();
  }, []); // Empty dependency array - only run once on mount

  const login = async (email, password) => {
    try {
      setLoading(true);
      setError(null);

      const response = await authAPI.login(email, password);
      console.log('Login response:', response.data);

      if (response.status === 200) {
        const { token, user: userData, type } = response.data;
        
        console.log('Extracted from response:', { token, userData, type });

        // Store token in localStorage
        localStorage.setItem('authToken', token);

        // Also set the token as a cookie (in case backend doesn't do it)
        setAuthTokenCookie(token);

        // Set user data including role
        const userWithRole = { 
          ...userData, 
          token,
          role: type // Store the role/type from response
        };
        
        console.log('Setting user with role:', userWithRole);
        setUser(userWithRole);

        return { success: true, user: userWithRole };
      } else {
        throw new Error('Login failed');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Login failed. Please try again.';
      setError(errorMessage);
      return { success: false, message: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      setLoading(true);
      await authAPI.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Always clear local state regardless of API call success
      localStorage.removeItem('authToken');
      setUser(null);
      setError(null);
      setLoading(false);
    }
  };

  const clearError = () => {
    setError(null);
  };

  const value = {
    user,
    loading,
    error,
    login,
    logout,
    clearError,
    updateUserFromVerification,
    isAuthenticated: !!user,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
